import { useCallback, useEffect, useState } from "react"
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material"
import ReceiptIcon from "@mui/icons-material/ReceiptLongOutlined"
import OpenIcon from "@mui/icons-material/OpenInNewOutlined"
import SendIcon from "@mui/icons-material/SendOutlined"
import BlockIcon from "@mui/icons-material/BlockOutlined"
import PayIcon from "@mui/icons-material/CreditCardOutlined"
import RetryIcon from "@mui/icons-material/ReplayOutlined"
import WarnIcon from "@mui/icons-material/ErrorOutlineOutlined"

import { billingApi } from "../../api"
import { usePermissions } from "../../permissions"
import EmptyState from "../../components/EmptyState"
import { LoadingRows } from "../../components/LoadingState"
import { decimalHours } from "../../time/duration"
import { formatDate } from "../../utils/format"
import { rise } from "../../motion"
import IssueInvoiceDialog from "./IssueInvoiceDialog"

/** Cents to a currency string. Integer in, formatted out — never the other way. */
export function money(cents, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format((Number(cents) || 0) / 100)
}

/** Stripe's vocabulary, coloured. `open` is neutral rather than red: an invoice
 *  that has been sent and not yet paid is the normal state, not a problem. */
export const STATUS_COLORS = {
  draft: "default",
  open: "info",
  paid: "success",
  void: "default",
  uncollectible: "error",
}

/**
 * Every invoice this caller may see.
 *
 * One component for both sides of the relationship: the back office sees drafts
 * and the buttons that issue and void, a client sees issued invoices and the
 * button that pays. The server decides which rows come back — this decides which
 * controls are worth rendering.
 */
export default function InvoiceTable({ onNotice, onError, stripe }) {
  const { can } = usePermissions()
  const mayInvoice = can("billing", "invoice")
  const mayVoid = can("billing", "void")

  const [invoices, setInvoices] = useState([])
  const [totals, setTotals] = useState({ outstanding_cents: 0, paid_cents: 0 })
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(null)
  const [busyId, setBusyId] = useState(null)
  const [issuing, setIssuing] = useState(null)
  /* "all" or "failed". Not a status, because failure is not one — see the
     route. */
  const [view, setView] = useState("all")
  const [syncing, setSyncing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await billingApi.invoices({ limit: 100, ...(view === "failed" ? { state: "failed" } : {}) })
      setInvoices(data.invoices || [])
      setTotals({
        outstanding_cents: data.outstanding_cents || 0,
        paid_cents: data.paid_cents || 0,
        failing: data.failing || 0,
      })
    } catch (err) {
      onError?.(err.userMessage)
    } finally {
      setLoading(false)
    }
  }, [onError, view])

  useEffect(() => {
    load()
  }, [load])

  /*
   * Try again means two different things, and the invoice knows which.
   *
   * One that was emailed needs the email sending again; one we were supposed to
   * collect needs the card charging again. The server reads `collection_method`
   * off the invoice rather than the client's current setting, so this button
   * does not have to decide — but the label does, because pressing something
   * called "Retry" and having money move is not a surprise anybody should get.
   */
  const reconcile = async () => {
    setSyncing(true)
    try {
      const result = await billingApi.reconcile()
      onNotice?.(result.message)
      load()
    } catch (err) {
      onError?.(err.userMessage)
    } finally {
      setSyncing(false)
    }
  }

  const retry = async (invoice) => {
    setBusyId(invoice.id)
    try {
      const result = await billingApi.retry(invoice.id)
      onNotice?.(result.message)
      load()
    } catch (err) {
      onError?.(err.userMessage)
    } finally {
      setBusyId(null)
    }
  }

  const discard = async (invoice) => {
    if (!window.confirm(`Discard draft ${invoice.number}? Its hours become billable again.`)) return
    try {
      const result = await billingApi.discardDraft(invoice.id)
      onNotice?.(result.message)
      load()
    } catch (err) {
      onError?.(err.userMessage)
    }
  }

  const voidInvoice = async (invoice) => {
    const reason = window.prompt(`Why is ${invoice.number} being voided?`)
    if (reason === null) return
    try {
      const result = await billingApi.void(invoice.id, reason)
      onNotice?.(result.message)
      load()
    } catch (err) {
      onError?.(err.userMessage)
    }
  }

  const failing = Number(totals.failing) || 0

  const filters = mayInvoice ? (
    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
      <Button size="small" variant={view === "all" ? "contained" : "outlined"} onClick={() => setView("all")}>
        All
      </Button>
      {/* Carries its own count, taken without the filter applied — a number that
          only appears once you are already looking at it warns nobody. */}
      <Button
        size="small"
        color={failing > 0 ? "error" : "inherit"}
        variant={view === "failed" ? "contained" : "outlined"}
        startIcon={<WarnIcon />}
        onClick={() => setView("failed")}
      >
        Needs attention{failing > 0 ? ` · ${failing}` : ""}
      </Button>
      <Box sx={{ flex: 1 }} />
      {/* Because the moment somebody needs this is the moment they are looking
          at an invoice that says open and a Stripe dashboard that says paid, and
          "wait up to an hour" is not an answer. */}
      <Tooltip title="Ask Stripe what actually happened to everything still marked open">
        <span>
          <Button size="small" color="inherit" startIcon={<RetryIcon />} disabled={syncing} onClick={reconcile}>
            {syncing ? "Checking…" : "Check with Stripe"}
          </Button>
        </span>
      </Tooltip>
    </Stack>
  ) : null

  if (loading) {
    return (
      <>
        {filters}
        <Card><CardContent><LoadingRows rows={5} /></CardContent></Card>
      </>
    )
  }
  if (invoices.length === 0) {
    return (
      <>
        {filters}
        <EmptyState
          icon={ReceiptIcon}
          title={view === "failed" ? "Nothing needs attention" : "No invoices yet"}
          description={
            view === "failed"
              ? "Nothing has been declined or failed to send."
              : mayInvoice
                ? "Draft one from the Prepare tab, or raise a one-off."
                : "Invoices appear here once they are sent."
          }
        />
      </>
    )
  }

  return (
    <>
      {filters}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <Card sx={{ flex: 1, minWidth: 180 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
            <Typography variant="h2" component="p">{money(totals.outstanding_cents)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: 180 }}>
          <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography variant="h2" component="p">{money(totals.paid_cents)}</Typography>
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Issued</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((invoice, index) => (
                  <TableRow
                    key={invoice.id}
                    hover
                    /* 140ms was a number picked by hand before there was
                       a scale to pick from. */
                    sx={{ cursor: "pointer", ...rise(index) }}
                    onClick={() => setOpen(invoice.id)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{invoice.number}</Typography>
                      {invoice.unsigned_seconds > 0 ? (
                        <Tooltip title="Some of the hours on this invoice were never signed off">
                          <Typography variant="caption" color="warning.main" display="block">
                            {decimalHours(invoice.unsigned_seconds)} h unsigned
                          </Typography>
                        </Tooltip>
                      ) : null}
                      {/* The reason, on the row. Stripe's message names the
                          difference between a card that expired and one with no
                          money on it, and those send somebody to two different
                          places — hiding it behind a click means the retry is
                          pressed first and understood afterwards. */}
                      {invoice.stripe_last_error ? (
                        <Typography variant="caption" color="error.main" display="block" sx={{ maxWidth: 320 }}>
                          {invoice.stripe_last_error}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell><Typography variant="body2">{invoice.client_label}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{invoice.issued_at ? formatDate(invoice.issued_at) : "—"}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{invoice.due_at ? formatDate(invoice.due_at) : "—"}</Typography></TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                      {money(invoice.total_cents, invoice.currency)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={invoice.status} color={STATUS_COLORS[invoice.status] || "default"} sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }} />
                        {/* `open` covers both "sent an hour ago" and "the card
                            was declined", which is the whole reason this column
                            could not be read. */}
                        {invoice.payment_failed_at ? (
                          <Tooltip title={`Declined ${invoice.payment_attempts > 1 ? `${invoice.payment_attempts} times` : "once"} — Stripe keeps trying on its own schedule`}>
                            <Chip size="small" color="error" label="Declined" sx={{ height: 20, fontSize: 11 }} />
                          </Tooltip>
                        ) : null}
                        {invoice.collection_method === "charge_automatically" ? (
                          <Tooltip title="Collected from the card on file">
                            <Chip size="small" variant="outlined" icon={<PayIcon sx={{ fontSize: 12 }} />} label="Auto" sx={{ height: 20, fontSize: 10 }} />
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {/*
                          A client's one action — but only when it is theirs to take.
                          
                          An invoice being collected automatically is not waiting on
                          them, and a Pay button on one asks somebody to do by hand
                          the thing they already authorised us to do for them. Worse,
                          if they press it while the charge is in flight they are
                          trying to pay an invoice Stripe is already paying. It comes
                          back only once the card has actually failed, which is the
                          one moment paying by hand is the right answer.
                        */}
                        {invoice.hosted_invoice_url &&
                        invoice.status === "open" &&
                        !mayInvoice &&
                        (invoice.collection_method !== "charge_automatically" || invoice.payment_failed_at) ? (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<PayIcon />}
                            href={invoice.hosted_invoice_url}
                            target="_blank"
                            rel="noopener"
                          >
                            {invoice.payment_failed_at ? "Pay another way" : "Pay"}
                          </Button>
                        ) : null}
                        {invoice.status === "open" &&
                        !mayInvoice &&
                        invoice.collection_method === "charge_automatically" &&
                        !invoice.payment_failed_at ? (
                          <Tooltip title="This is collected from the card you authorised. Nothing for you to do.">
                            <Typography variant="caption" color="text.secondary">
                              Charging automatically
                            </Typography>
                          </Tooltip>
                        ) : null}
                        {invoice.hosted_invoice_url && mayInvoice ? (
                          <Tooltip title="Open the Stripe invoice">
                            <Button size="small" href={invoice.hosted_invoice_url} target="_blank" rel="noopener" startIcon={<OpenIcon />}>
                              Stripe
                            </Button>
                          </Tooltip>
                        ) : null}
                        {mayInvoice && invoice.status === "draft" ? (
                          <>
                            {/* Opens the question rather than answering it: see
                                IssueInvoiceDialog. */}
                            <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={() => setIssuing(invoice)}>
                              Send
                            </Button>
                            <Button size="small" color="inherit" onClick={() => discard(invoice)}>Discard</Button>
                          </>
                        ) : null}
                        {mayInvoice && invoice.status === "open" && (invoice.payment_failed_at || view === "failed") ? (
                          <Tooltip
                            title={
                              invoice.collection_method === "charge_automatically"
                                ? "Charge the card again now, rather than waiting for Stripe's next attempt"
                                : "Send the payment email again"
                            }
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color={invoice.collection_method === "charge_automatically" ? "primary" : "inherit"}
                                startIcon={<RetryIcon />}
                                disabled={busyId === invoice.id}
                                onClick={() => retry(invoice)}
                              >
                                {busyId === invoice.id
                                  ? "Trying…"
                                  : invoice.collection_method === "charge_automatically"
                                    ? "Charge again"
                                    : "Resend"}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : null}
                        {mayVoid && invoice.status === "open" ? (
                          <Tooltip title="Void this invoice">
                            <Button size="small" color="inherit" startIcon={<BlockIcon />} onClick={() => voidInvoice(invoice)}>Void</Button>
                          </Tooltip>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      <InvoiceDialog invoiceId={open} onClose={() => setOpen(null)} />
      <IssueInvoiceDialog
        invoice={issuing}
        onClose={() => setIssuing(null)}
        onError={onError}
        onIssued={(message) => {
          setIssuing(null)
          onNotice?.(message)
          load()
        }}
      />
    </>
  )
}

/** One invoice, its lines, and the hours behind them. */
function InvoiceDialog({ invoiceId, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!invoiceId) return
    setLoading(true)
    billingApi
      .invoice(invoiceId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [invoiceId])

  const invoice = data?.invoice

  return (
    <Dialog open={Boolean(invoiceId)} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {invoice?.number || "Invoice"}
        {invoice ? (
          <Typography variant="body2" color="text.secondary">
            {invoice.client_label} · {money(invoice.total_cents, invoice.currency)} · {invoice.status}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        {loading || !data ? (
          <LoadingRows rows={4} />
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.lines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{line.quantity}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(line.unit_amount_cents, invoice.currency)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{money(line.amount_cents, invoice.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Stack spacing={0.5} sx={{ ml: "auto", maxWidth: 260, mt: 2 }}>
              <Line label="Subtotal" value={money(invoice.subtotal_cents, invoice.currency)} />
              {invoice.discount_cents > 0 ? <Line label="Discount" value={`−${money(invoice.discount_cents, invoice.currency)}`} /> : null}
              {invoice.tax_cents > 0 ? <Line label="Tax" value={money(invoice.tax_cents, invoice.currency)} /> : null}
              <Divider />
              <Line label="Total" value={money(invoice.total_cents, invoice.currency)} bold />
              {invoice.amount_paid_cents > 0 ? <Line label="Paid" value={money(invoice.amount_paid_cents, invoice.currency)} /> : null}
            </Stack>

            {invoice.notes ? (
              <Alert severity="info" sx={{ mt: 2 }}>{invoice.notes}</Alert>
            ) : null}

            {data.entries.length > 0 ? (
              <>
                <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
                  The hours behind it
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {data.time_visibility === "detailed" ? "detailed" : "summarised"}
                  </Typography>
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      {data.time_visibility === "detailed" ? <TableCell>Who</TableCell> : null}
                      {data.time_visibility === "detailed" ? <TableCell>Work</TableCell> : null}
                      <TableCell align="right">Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell><Typography variant="caption">{formatDate(entry.started_at)}</Typography></TableCell>
                        {data.time_visibility === "detailed" ? <TableCell><Typography variant="caption">{entry.user_name}</Typography></TableCell> : null}
                        {data.time_visibility === "detailed" ? <TableCell><Typography variant="caption">{entry.description || "—"}</Typography></TableCell> : null}
                        <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>{decimalHours(entry.billable_seconds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : null}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {invoice?.invoice_pdf_url ? (
          <Button href={invoice.invoice_pdf_url} target="_blank" rel="noopener" startIcon={<OpenIcon />}>PDF</Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

function Line({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400} sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
    </Stack>
  )
}
